// src/services/dashboardApi.ts
import api from './api';
import { Device, SystemStatusResponse, SystemStatusMetrics } from '../types/devices';
import { Asset } from '../types/assets';
import { Playlist } from '../types/playlists';
import { parseUTCDate } from '../utils/dateUtils';

export interface DashboardStats {
  totalDevices: number;
  activeDevices: number;
  onlineDevices: number;
  totalPlaylists: number;
  activePlaylists: number;
  totalAssets: number;
  totalStorage: number;
  deviceStatuses: DeviceStatusSummary[];
}

export interface DeviceStatusSummary {
  deviceId: number;
  deviceName: string;
  location: string;
  status: string;
  currentPlaylist?: string;
  lastSync?: Date;
  metrics?: SystemStatusMetrics;
  statusReasons?: string[];
}

export const dashboardApi = {
  getDashboardStats: async (): Promise<DashboardStats> => {
    try {
      // Fetch all required data in parallel
      const [devices, playlists, assets, systemStatuses] = await Promise.all([
        api.get<Device[]>('/devices'),
        api.get<Playlist[]>('/playlists'),
        api.get<Asset[]>('/assets'),
        api.get<SystemStatusResponse[]>('/systemstatus/all')  // Specify the type here
      ]);

      // Calculate statistics
      const totalDevices = devices.data.length;
      const activeDevices = devices.data.filter(d => d.active).length;
      const onlineDevices = devices.data.filter(d => d.onlineStatus === 'Online').length;
      const totalPlaylists = playlists.data.length;
      const activePlaylists = playlists.data.filter(p => p.active).length;
      const totalAssets = assets.data.length;
      const totalStorage = assets.data.reduce((sum, asset) => sum + asset.fileSize, 0);

      // Map system statuses to devices
      const deviceStatuses: DeviceStatusSummary[] = systemStatuses.data.map((status: SystemStatusResponse) => ({
        deviceId: status.deviceId,
        deviceName: status.deviceName,
        location: status.location,
        status: status.status,
        statusReasons: status.statusReasons,
        currentPlaylist: status.currentPlaylist,
        lastSync: status.lastSync ? parseUTCDate(status.lastSync) : undefined,
        metrics: status.metrics ? {
          cpuUsage: status.metrics.cpuUsage,
          memoryUsage: status.metrics.memoryUsage,
          diskUsage: status.metrics.diskUsage,
          temperature: status.metrics.temperature
        } : undefined
      }));

      return {
        totalDevices,
        activeDevices,
        onlineDevices,  
        totalPlaylists,
        activePlaylists,
        totalAssets,
        totalStorage,
        deviceStatuses
      };
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      throw error;
    }
  }
};